import { useRef, useState } from "react";
import { Upload, X, Camera, ArrowLeft, ArrowRight } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import { toast } from "sonner";

/**
 * Manage a list of image URLs (gallery). Supports upload, reorder, remove.
 * Props:
 *  - value: string[] (list of image URLs)
 *  - onChange: (next: string[]) => void
 *  - max: number (default 8)
 *  - testid: string
 */
export default function MultiImageUpload({ value = [], onChange, max = 8, testid = "multi-image-upload" }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const items = Array.isArray(value) ? value : [];

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    const remaining = Math.max(0, max - items.length);
    const toUpload = Array.from(files).slice(0, remaining);
    if (toUpload.length === 0) {
      toast.error(`Maximum ${max} images`);
      return;
    }
    setUploading(true);
    const next = [...items];
    try {
      for (const file of toUpload) {
        if (file.size > 8 * 1024 * 1024) {
          toast.error(`${file.name}: too large (max 8MB)`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push(r.data.url);
      }
      onChange(next);
      toast.success(`${toUpload.length} image(s) added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
  };

  const moveLeft = (idx) => {
    if (idx <= 0) return;
    const next = [...items];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };

  const moveRight = (idx) => {
    if (idx >= items.length - 1) return;
    const next = [...items];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      {items.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
          {items.map((src, i) => (
            <div key={`${src}-${i}`} className="relative group" data-testid={`${testid}-item-${i}`}>
              <img
                src={resolveImage(src)}
                alt={`Gallery ${i + 1}`}
                className="w-full h-24 object-cover rounded-lg border border-[var(--dsf-border)]"
              />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-[var(--dd-coral)] text-white flex items-center justify-center rounded-full shadow-md"
                data-testid={`${testid}-remove-${i}`}
                title="Remove"
              >
                <X size={11} />
              </button>
              <div className="absolute bottom-1 left-1 right-1 flex justify-between opacity-0 group-hover:opacity-100 transition">
                <button
                  type="button"
                  onClick={() => moveLeft(i)}
                  disabled={i === 0}
                  className="w-6 h-6 bg-black/70 text-white rounded-full flex items-center justify-center disabled:opacity-30"
                  data-testid={`${testid}-up-${i}`}
                  title="Move left"
                >
                  <ArrowLeft size={11} />
                </button>
                <button
                  type="button"
                  onClick={() => moveRight(i)}
                  disabled={i === items.length - 1}
                  className="w-6 h-6 bg-black/70 text-white rounded-full flex items-center justify-center disabled:opacity-30"
                  data-testid={`${testid}-down-${i}`}
                  title="Move right"
                >
                  <ArrowRight size={11} />
                </button>
              </div>
              {i === 0 && (
                <div className="absolute top-1 left-1 bg-[var(--dd-mustard)] text-[var(--dd-green-deep)] text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                  Cover
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || items.length >= max}
          className="flex items-center gap-2 px-4 py-2.5 border border-[var(--dsf-border)] hover:border-[var(--dd-green)] text-sm rounded-full transition-colors disabled:opacity-50"
          data-testid={`${testid}-pick`}
        >
          <Upload size={14} /> {uploading ? "Uploading…" : "Add images"}
        </button>
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          disabled={uploading || items.length >= max}
          className="flex items-center gap-2 px-4 py-2.5 border border-[var(--dsf-border)] hover:border-[var(--dd-green)] text-sm rounded-full transition-colors disabled:opacity-50"
          data-testid={`${testid}-camera`}
        >
          <Camera size={14} /> Take photo
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div className="text-[10px] text-[var(--dsf-text-muted)]">
        {items.length}/{max} images · JPG/PNG, max 8MB each. First image is the cover.
      </div>
    </div>
  );
}
